import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import ReactGA from 'react-ga'
import { Button } from 'reactstrap';
import { CSSTransition } from "react-transition-group";

import LoadingIndicator from '../../components/loading-indicator'
import { MemberSearch } from '../../components/teams'
import { getCurrentTeam, teamActions } from '../../../core/teams'
import { membersListActions, getCurrentMembersList, getMembersForCurrentMembersList } from '../../../core/members-list'
import { teamsReportListActions } from '../../../core/teams-report'
import { DEFAULT_SESSION_ID } from '../../../core/constants'
import { trackingActions } from '../../../core/tracking';
import { useNavigate, useParams } from 'react-router-dom';

const EditPage = () => {
  const { id } = useParams();
  const team_id = id || DEFAULT_SESSION_ID;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const nodeRef = useRef(null);

  const user = useSelector(state => state.user);
  const team = useSelector(state => getCurrentTeam(state));
  const members_list = useSelector(state => getCurrentMembersList(state));
  const members = useSelector(state => getMembersForCurrentMembersList(state));

  const [currentSection, setCurrentSection] = useState('search');
  const [animationClass] = useState({
    appearActive: 'animate__animated animate__slideInRight position-fixed',
    enter: 'animate__animated',
    enterActive: 'animate__animated animate__slideInRight position-fixed',
    exit: 'animate__animated',
    exitActive: 'animate__animated animate__slideOutLeft position-fixed',
  });
  const [filters] = useState({
    name: ''
  });
  const [membersSelected, setMembersSelected] = useState(new Set());
  const [currentMemberId, setCurrentMemberId] = useState(null);
  const [quadrantName, setQuadrantName] = useState(null);
  const [addingMembers, setAddingMembers] = useState(false);

  const displayLoadingIndicator = team.isPending || members_list.isPending || false;

  useEffect(() => {
    if (!team || team.id !== team_id) {
      dispatch(teamActions.loadTeam(team_id));
    }

    if (team_id !== DEFAULT_SESSION_ID && !team.name) {
      dispatch(teamsReportListActions.loadTeamsReport(team_id));
    }

    dispatch(membersListActions.findMembers({
      not_team_id: team_id
    }));
  }, [dispatch, team_id, team]);

  const hanndleNextSection = useCallback(({ section = 'search', filters = null, member_id = null, quadrant_name = null }) => {
    if (section === 'add-member') {
      setCurrentSection('search');
      setCurrentMemberId(null);

      if (!membersSelected.has(member_id)) {
        const newSelected = new Set(membersSelected);
        newSelected.add(member_id);
        setMembersSelected(newSelected);
      }
    }

    if (section === 'save-members') {
      setCurrentSection('search');
      dispatch(trackingActions.saveTrackingEvent({ category: 'Teams', action: 'Add members completed' }));
      
      ReactGA.event({
        category: 'Teams',
        action: 'Add members completed'
      });
      
      setAddingMembers(true);
      dispatch(teamActions.addMembers({ items: membersSelected, team_id: team.id }));
    }

    setCurrentSection(section);
    setCurrentMemberId(member_id);
    setQuadrantName(quadrant_name);
  }, [dispatch, membersSelected, team.id]);

  const toggleCheckbox = useCallback((member_id) => {
    setMembersSelected(prevSelected => {
      const newSelected = new Set(prevSelected);
      const availableMemberIds = members.map(m => m.id);
      const totalAvailable = availableMemberIds.length;

      if (member_id === 'all') {
        // seleccionamos / deseleccionamos todos
        if (newSelected.has('all')) {
          newSelected.clear();
        } else {
          members.forEach(m => newSelected.add(m.id));
          newSelected.add('all');
        }
      } else {
        if (newSelected.has(member_id)) {
          newSelected.delete(member_id);
          newSelected.delete('all');
        } else {
          newSelected.add(member_id);

          const selectedRegular = [...newSelected].filter(id => id !== 'all');
          if (selectedRegular.length === totalAvailable) {
            // Si se seleccionaron todos manualmente, agregar 'all' también
            members.forEach(m => newSelected.add(m.id));
            newSelected.add('all');
          }
        }
      }

      return newSelected;
    });
  }, [members]);

  const handleBack = useCallback((e) => {
    e.preventDefault();
    navigate(-1);
  }, [navigate]);

  if (user.checked && !user.id) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <div className="container-fluid">
        <div className="view overflow-hidden team-add-member">
          <div>
            <CSSTransition nodeRef={nodeRef} classNames={animationClass} timeout={{ enter: 1000, exit: 1000 }} key={currentSection} appear>
              <div ref={nodeRef} className="w-100 h-100 d-flex flex-column">
                <div className='mb-3 pt-md-4'>
                  <h1 className='mb-2 mb-lg-0 libre-caslon-text-regular h3-md h4'>Teams</h1>
                </div>

                <div className="position-relative d-flex justify-content-between align-items-center mb-3">
                  <Button color='link' onClick={handleBack} className="p-0 text-body mr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width={28} height={29} viewBox="0 0 28 29" fill='currentColor'>
                      <path d="M24.5,14.5c0,0.2-0.1,0.5-0.3,0.6c-0.2,0.2-0.4,0.3-0.6,0.3H6.5l6.4,6.4c0.1,0.1,0.1,0.2,0.2,0.3
                        c0,0.1,0.1,0.2,0.1,0.3s0,0.2-0.1,0.3c0,0.1-0.1,0.2-0.2,0.3c-0.1,0.1-0.2,0.1-0.3,0.2c-0.1,0-0.2,0.1-0.3,0.1c-0.1,0-0.2,0-0.3-0.1
                        c-0.1,0-0.2-0.1-0.3-0.2l-7.9-7.9c-0.1-0.1-0.1-0.2-0.2-0.3c0-0.1-0.1-0.2-0.1-0.3c0-0.1,0-0.2,0.1-0.3c0-0.1,0.1-0.2,0.2-0.3
                        L11.6,6c0.2-0.2,0.4-0.3,0.6-0.3c0.2,0,0.5,0.1,0.6,0.3c0.2,0.2,0.3,0.4,0.3,0.6c0,0.2-0.1,0.5-0.3,0.6l-6.4,6.4h17.1
                        c0.2,0,0.5,0.1,0.6,0.3C24.4,14,24.5,14.3,24.5,14.5z"/>
                    </svg>
                  </Button>
                  <div className="flex-grow-1">
                    <h2 className="mb-0 h4 h3-md">Add members to {team.name}</h2>
                  </div>
                </div>

                <MemberSearch 
                  hanndleNextSection={hanndleNextSection}
                  members={members}
                  handleCheckboxChange={toggleCheckbox}
                  members_selected={membersSelected}
                  team_id={team.id}
                />
                
                {(displayLoadingIndicator || addingMembers) && <LoadingIndicator/>}
              </div>
            </CSSTransition>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditPage;